import React, { useState } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Button,
  Typography,
  Collapse,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ThumbUpIcon,
  ThumbUpSolidIcon,
  ThumbDownIcon,
  ThumbDownSolidIcon,
  CheckIcon,
} from '../ui/Icons';
import { feedbackService } from '../../services/feedback/feedbackService';
import type { FeedbackRating, WorkflowStep } from '../../types/feedback';

interface MessageFeedbackProps {
  /** Project ID for the feedback */
  projectId: string;
  /** Workflow step (discovery, design, etc.) */
  step: WorkflowStep;
  /** Index of the message in chat history */
  messageIndex: number;
  /** Previously submitted feedback rating (if any) */
  existingFeedback?: FeedbackRating;
  /** Callback when feedback is successfully submitted */
  onFeedbackSubmit?: (rating: FeedbackRating) => void;
  /** Disable the feedback buttons */
  disabled?: boolean;
}

type FeedbackState = 'idle' | 'selecting' | 'submitting' | 'submitted';

const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  projectId,
  step,
  messageIndex,
  existingFeedback,
  onFeedbackSubmit,
  disabled = false,
}) => {
  const theme = useTheme();
  const [state, setState] = useState<FeedbackState>(
    existingFeedback ? 'submitted' : 'idle'
  );
  const [selectedRating, setSelectedRating] = useState<FeedbackRating | null>(
    existingFeedback || null
  );
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRatingClick = (rating: FeedbackRating) => {
    if (state === 'submitted' || disabled) return;

    setSelectedRating(rating);
    setShowComment(true);
    setState('selecting');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedRating) return;

    setState('submitting');
    setError(null);

    try {
      await feedbackService.submitFeedback({
        project_id: projectId,
        step,
        message_index: messageIndex,
        rating: selectedRating,
        comment: comment.trim() || undefined,
      });

      setState('submitted');
      setShowComment(false);
      onFeedbackSubmit?.(selectedRating);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to submit feedback'
      );
      setState('selecting');
    }
  };

  const handleCancel = () => {
    setSelectedRating(null);
    setComment('');
    setShowComment(false);
    setState('idle');
    setError(null);
  };

  const isSubmitting = state === 'submitting';
  const isSubmitted = state === 'submitted';

  // If already submitted, show simple confirmation
  if (isSubmitted && selectedRating) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 1,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          bgcolor:
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.03)',
        }}
      >
        <CheckIcon size={14} className="text-green-600" />
        <Typography variant="caption" color="text.secondary">
          {selectedRating === 'positive' ? 'Helpful' : 'Not helpful'} - Thanks!
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {/* Rating buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Was this helpful?
        </Typography>

        <Tooltip title="Helpful">
          <span>
            <IconButton
              size="small"
              onClick={() => handleRatingClick('positive')}
              disabled={disabled || isSubmitting}
              sx={{
                p: 0.5,
                color:
                  selectedRating === 'positive'
                    ? theme.palette.success.main
                    : theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.success.main,
                  bgcolor: theme.palette.success.light + '20',
                },
              }}
            >
              {selectedRating === 'positive' ? (
                <ThumbUpSolidIcon size={16} />
              ) : (
                <ThumbUpIcon size={16} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Not helpful">
          <span>
            <IconButton
              size="small"
              onClick={() => handleRatingClick('negative')}
              disabled={disabled || isSubmitting}
              sx={{
                p: 0.5,
                color:
                  selectedRating === 'negative'
                    ? theme.palette.error.main
                    : theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.error.main,
                  bgcolor: theme.palette.error.light + '20',
                },
              }}
            >
              {selectedRating === 'negative' ? (
                <ThumbDownSolidIcon size={16} />
              ) : (
                <ThumbDownIcon size={16} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Comment input (shown after rating selection) */}
      <Collapse in={showComment}>
        <Box sx={{ mt: 1, pl: 1 }}>
          <TextField
            size="small"
            multiline
            rows={2}
            placeholder="Add a comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting}
            fullWidth
            sx={{ mb: 1 }}
            inputProps={{ maxLength: 1000 }}
          />

          {error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mb: 1 }}
            >
              {error}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="text"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedRating}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MessageFeedback;
